import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ClipboardList } from 'lucide-react';
import { inv } from '../../lib/invApi';
import { toast } from 'sonner';
import { SupplierOrderDialog, NewOrderSheetButton, STATUS_LABEL, STATUS_CLS } from '../../components/inventory/SupplierOrderDialog';

const sel = 'h-9 rounded-md bg-[#0A0A0A] border border-[#262626] text-white text-sm px-2';
const fmt = (d) => (d ? new Date(d).toLocaleString('en-US', { timeZone: 'America/New_York', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—');

// Supplier Orders — tracked order sheets (from the Reorder List or manual). Not PO automation.
const InventorySupplierOrders = () => {
  const [params, setParams] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState('');
  const [suppliers, setSuppliers] = useState([]);
  const openId = params.get('open');
  const load = useCallback(() => inv.get('/supplier-orders', { status: status || undefined }).then(({ data }) => setRows(data)).catch(() => toast.error('Could not load order sheets')), [status]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { inv.get('/suppliers').then(({ data }) => setSuppliers(data)).catch(() => {}); }, []);
  const open = (id) => setParams(id ? { open: id } : {});

  return (
    <div data-testid="inv-supplier-orders-page" className="space-y-5">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-['Outfit'] text-3xl font-bold text-white mb-1 flex items-center gap-3"><ClipboardList className="w-7 h-7 text-[#D4AF37]" /> Supplier Orders</h2>
          <p className="text-[#A3A3A3] text-sm">Printable, emailable order sheets with a tracked history. Create them from the Reorder List or manually. Stock only changes when material is received.</p>
        </div>
        <div className="flex gap-2 items-center">
          <select data-testid="so-filter-status" value={status} onChange={(e) => setStatus(e.target.value)} className={sel}>
            <option value="">All statuses</option>
            {Object.entries(STATUS_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <NewOrderSheetButton suppliers={suppliers} onCreated={(o) => { load(); open(o.id); }} />
        </div>
      </div>
      <div className="bg-[#141414] border border-[#262626] rounded-md overflow-x-auto">
        <table className="w-full text-sm min-w-[760px]" data-testid="so-table">
          <thead><tr className="text-[#A3A3A3] border-b border-[#262626] bg-[#0A0A0A]">
            {['Order', 'Supplier', 'Status', 'Lines', 'Total Qty', 'Est. Value', 'Source', 'Created', 'Sent'].map((h) => <th key={h} className="px-3 py-2 text-left font-medium">{h}</th>)}
          </tr></thead>
          <tbody>
            {rows.length === 0 ? <tr><td colSpan={9} className="px-3 py-6 text-center text-[#525252]">No order sheets yet.</td></tr> : rows.map((r) => (
              <tr key={r.id} data-testid={`so-row-${r.id}`} onClick={() => open(r.id)} className="border-b border-[#262626] last:border-0 cursor-pointer hover:bg-[#1a1a1a]">
                <td className="px-3 py-2 text-[#D4AF37] font-semibold">{r.order_number}</td>
                <td className="px-3 py-2 text-white">{r.supplier}</td>
                <td className="px-3 py-2"><span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${STATUS_CLS[r.status]}`}>{STATUS_LABEL[r.status]}</span></td>
                <td className="px-3 py-2 text-[#A3A3A3]">{r.lines.length}</td>
                <td className="px-3 py-2 text-[#A3A3A3]">{r.total_qty}</td>
                <td className="px-3 py-2 text-[#A3A3A3]">${r.est_value.toFixed(2)}</td>
                <td className="px-3 py-2 text-[#A3A3A3]">{r.source === 'reorder_list' ? 'Reorder List' : 'Manual'}</td>
                <td className="px-3 py-2 text-[#A3A3A3] whitespace-nowrap">{fmt(r.created_at)} · {r.created_by_name}</td>
                <td className="px-3 py-2 text-[#A3A3A3] whitespace-nowrap">{r.sent_at ? `${fmt(r.sent_at)}${r.sent_to ? ` → ${r.sent_to}` : ''}` : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {openId && <SupplierOrderDialog orderId={openId} onClose={() => open(null)} onChanged={load} />}
    </div>
  );
};

export default InventorySupplierOrders;
